import { useState, useEffect, useMemo } from 'react'
import {
  Calculator,
  X,
  ChefHat,
  Scale,
  ArrowRight,
  Check,
  Info,
  Printer,
  FileCode,
  Download,
  Beef,
  Carrot,
  Wheat,
  Droplets
} from 'lucide-react'
import { useApp } from '../App'
import { menusAPI } from '../lib/api'
import { buildHavnContext, mdTable, downloadMD, buildFilename, fmtNum } from '../lib/mdExport'

// ══════════════════════════════════════════════════════════════════════════════
// SMART BATCH CALCULATOR - Prep List Generator
// ══════════════════════════════════════════════════════════════════════════════
//
// Innovative feature: Takes meal counts and calculates optimal batch sizes for
// each component. Accounts for recipe scaling, rounds up to practical batches,
// and generates a prep list that kitchen staff can use.
//

// Component category icons
const categoryIcons = {
  proteins: Beef,
  vegetables: Carrot,
  carbs: Wheat,
  sauces: Droplets,
  bases: ChefHat,
}

const categoryColors = {
  proteins: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  vegetables: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  carbs: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  sauces: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  bases: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
}

function BatchItem({ item, category, onToggleComplete }) {
  const Icon = categoryIcons[category] || ChefHat
  const colors = categoryColors[category] || categoryColors.bases

  // Calculate optimal batch count
  const batchSize = item.recipe_batch_lbs || 5 // Default 5 lb batches
  const batchesNeeded = Math.ceil(item.total_lbs / batchSize)
  const actualYield = batchesNeeded * batchSize
  const surplus = actualYield - item.total_lbs

  return (
    <div className={`p-4 rounded-xl border transition-all ${
      item.completed ? 'bg-emerald-50 border-emerald-200 opacity-60' : `${colors.bg} ${colors.border}`
    }`}>
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <button
          onClick={() => onToggleComplete(item.name)}
          className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
            item.completed
              ? 'bg-emerald-500 text-white'
              : 'border-2 border-havn-gray-300 hover:border-havn-green-700'
          }`}
        >
          {item.completed && <Check className="w-4 h-4" />}
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${colors.text}`} />
            <h4 className={`font-semibold ${item.completed ? 'line-through text-havn-gray-400' : 'text-havn-charcoal'}`}>
              {item.name}
            </h4>
          </div>

          <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
            <div>
              <span className="text-havn-gray-400 text-xs block">Needed</span>
              <span className="font-medium text-havn-charcoal">{item.total_lbs.toFixed(1)} lbs</span>
            </div>
            <div>
              <span className="text-havn-gray-400 text-xs block">Batches</span>
              <span className="font-medium text-havn-charcoal">
                {batchesNeeded} × {batchSize} lbs
              </span>
            </div>
            <div>
              <span className="text-havn-gray-400 text-xs block">Make</span>
              <span className="font-bold text-havn-green-700">{actualYield.toFixed(1)} lbs</span>
            </div>
          </div>

          {surplus > 0.5 && (
            <div className="mt-2 text-xs text-havn-gray-400 flex items-center gap-1">
              <Info className="w-3 h-3" />
              +{surplus.toFixed(1)} lbs extra (use for samples/waste buffer)
            </div>
          )}

          {item.dishes?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {item.dishes.slice(0, 3).map((dish, i) => (
                <span key={i} className="text-xs px-2 py-0.5 bg-white/60 rounded-full text-havn-gray-500">
                  {dish}
                </span>
              ))}
              {item.dishes.length > 3 && (
                <span className="text-xs px-2 py-0.5 bg-white/60 rounded-full text-havn-gray-400">
                  +{item.dishes.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default function BatchCalculator({ onClose }) {
  const { currentMenu } = useApp()
  const [prepList, setPrepList] = useState(null)
  const [loading, setLoading] = useState(true)
  const [completed, setCompleted] = useState({})
  const [filter, setFilter] = useState('all')

  // Load menu data and calculate batches
  useEffect(() => {
    async function loadData() {
      if (!currentMenu) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)

        // Get menu with dishes
        const menuRes = await fetch(`/api/menus/${currentMenu}`)
        const menuData = await menuRes.json()

        // Get ordering data (has all component calculations)
        const orderRes = await fetch(`/api/ordering/calculate/${currentMenu}`)
        const orderData = await orderRes.json()

        // Build prep list from ordering data
        // Note: Backend returns needed_with_buffer (not total_needed_lbs)
        // Categories: proteins, vegetables, carbs, sauces (not bases - those expand to additional)
        const categories = ['proteins', 'vegetables', 'carbs', 'sauces']
        const prepItems = {}

        categories.forEach(cat => {
          const items = orderData[cat] || []
          prepItems[cat] = items
            .filter(item => (item.needed_with_buffer || item.needed_raw || 0) > 0)
            .map(item => ({
              name: item.ingredient_name || item.display_name,
              total_lbs: item.needed_with_buffer || item.needed_raw || 0,
              recipe_batch_lbs: item.recipe_batch_lbs || (cat === 'sauces' ? 2 : 5),
              dishes: item.dishes?.map(d => d.name || d.dish_name || d) || [],
              completed: false,
            }))
            .sort((a, b) => b.total_lbs - a.total_lbs)
        })

        // Also include additional ingredients from recipes (dry goods, liquids, refrigerated)
        if (orderData.additional) {
          const additionalCategories = ['dry_goods', 'liquids', 'refrigerated', 'proteins']
          let allAdditional = []

          additionalCategories.forEach(addCat => {
            const items = orderData.additional[addCat] || []
            allAdditional = allAdditional.concat(
              items
                .filter(item => (item.needed_with_buffer || item.needed_raw || 0) > 0)
                .map(item => ({
                  name: item.ingredient_name || item.display_name,
                  total_lbs: item.needed_with_buffer || item.needed_raw || 0,
                  recipe_batch_lbs: 5,
                  dishes: item.source_recipes?.map(r => r.name) || [],
                  completed: false,
                }))
            )
          })

          // Group additional as "bases" for display
          prepItems.bases = allAdditional.sort((a, b) => b.total_lbs - a.total_lbs)
        }

        setPrepList(prepItems)
      } catch (err) {
        console.error('Failed to load prep data:', err)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [currentMenu])

  // Toggle completion
  const handleToggleComplete = (name) => {
    setCompleted(prev => ({
      ...prev,
      [name]: !prev[name]
    }))
  }

  // Calculate stats
  const stats = useMemo(() => {
    if (!prepList) return { total: 0, completed: 0, totalLbs: 0 }

    let total = 0
    let completedCount = 0
    let totalLbs = 0

    Object.values(prepList).forEach(items => {
      items.forEach(item => {
        total++
        totalLbs += item.total_lbs
        if (completed[item.name]) completedCount++
      })
    })

    return { total, completed: completedCount, totalLbs }
  }, [prepList, completed])

  // Filter prep list
  const filteredList = useMemo(() => {
    if (!prepList) return null
    if (filter === 'all') return prepList

    return { [filter]: prepList[filter] || [] }
  }, [prepList, filter])

  // Print prep list
  const handlePrint = () => {
    window.print()
  }

  // Export prep list as markdown for LLM consumption
  const handleExportMD = async () => {
    if (!prepList) return
    let menuName = null
    let cityId = null
    try {
      if (currentMenu) {
        const menu = await menusAPI.get(currentMenu)
        menuName = menu?.name
        cityId = menu?.city_id
      }
    } catch { /* best-effort menu lookup */ }

    let md = buildHavnContext({
      docType: 'Smart Batch Calculator — Prep List',
      docPurpose: 'Calculated batch sizes per component. Takes raw lbs needed (with buffer), rounds up to whole recipe batches, and shows surplus. Used by kitchen staff to plan how many batches of each thing to cook.',
      city: cityId,
      menu: menuName,
    })

    md += `# Prep List — Batch Sizes\n\n`
    md += `Total items: ${stats.total} · Total lbs: ${fmtNum(stats.totalLbs, 1)}\n\n`
    md += `> Each row shows: total needed, recipe's batch size, how many batches to cook, and the resulting yield (batches × batch size). Surplus is the difference between yield and need — typically used for samples or waste buffer.\n\n`

    const categoryOrder = ['proteins', 'vegetables', 'carbs', 'sauces', 'bases']
    categoryOrder.forEach((cat) => {
      const items = prepList[cat] || []
      if (!items.length) return
      md += `## ${cat.charAt(0).toUpperCase() + cat.slice(1)} (${items.length})\n\n`
      md += mdTable(
        ['Component', 'Needed (lbs)', 'Batch Size', 'Batches', 'Yield (lbs)', 'Surplus (lbs)', 'Dishes'],
        items.map((it) => {
          const batchSize = it.recipe_batch_lbs || 5
          const batches = Math.ceil(it.total_lbs / batchSize)
          const yieldLbs = batches * batchSize
          const surplus = yieldLbs - it.total_lbs
          return [
            it.name,
            fmtNum(it.total_lbs, 1),
            fmtNum(batchSize, 1),
            batches,
            fmtNum(yieldLbs, 1),
            fmtNum(surplus, 1),
            (it.dishes || []).join('; '),
          ]
        }),
      )
      md += `\n\n`
    })

    downloadMD(buildFilename('prep-batches', { city: cityId, menu: menuName }), md)
  }

  if (loading) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 text-center">
          <div className="spinner w-8 h-8 text-havn-green-700 mx-auto mb-4" />
          <p className="text-havn-gray-500">Calculating batches...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden animate-slide-up shadow-elevated flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-havn-green-800 to-havn-green-700 text-white flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Calculator className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold">Smart Batch Calculator</h3>
              <p className="text-sm text-white/70">
                {stats.completed}/{stats.total} items • {stats.totalLbs.toFixed(0)} lbs total
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              title="Print prep list"
            >
              <Printer className="w-5 h-5" />
            </button>
            <button
              onClick={handleExportMD}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              title="Export prep list as markdown for an LLM"
            >
              <FileCode className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-3 bg-havn-cream-50 border-b border-havn-cream-200 flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-havn-charcoal">Prep Progress</span>
            <span className="text-sm text-havn-gray-500">
              {Math.round((stats.completed / stats.total) * 100)}% complete
            </span>
          </div>
          <div className="h-2 bg-havn-cream-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-havn-green-600 to-emerald-500 transition-all duration-500"
              style={{ width: `${(stats.completed / stats.total) * 100}%` }}
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="px-6 py-3 border-b border-havn-cream-200 flex gap-2 overflow-x-auto flex-shrink-0">
          {['all', 'proteins', 'vegetables', 'carbs', 'sauces', 'bases'].map(cat => {
            const count = cat === 'all'
              ? stats.total
              : (prepList?.[cat]?.length || 0)

            const colors = cat === 'all'
              ? { bg: 'bg-havn-green-100', text: 'text-havn-green-700' }
              : categoryColors[cat]

            return (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  filter === cat
                    ? `${colors.bg} ${colors.text}`
                    : 'text-havn-gray-500 hover:bg-havn-cream-100'
                }`}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)} ({count})
              </button>
            )
          })}
        </div>

        {/* Prep List */}
        <div className="flex-1 overflow-y-auto p-6">
          {!prepList || stats.total === 0 ? (
            <div className="text-center py-12">
              <Calculator className="w-16 h-16 text-havn-gray-300 mx-auto mb-4" />
              <h4 className="font-display text-lg font-semibold text-havn-charcoal mb-2">
                No Menu Selected
              </h4>
              <p className="text-havn-gray-500">
                Select a menu with meal counts to generate your prep list
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(filteredList).map(([category, items]) => {
                if (items.length === 0) return null

                const Icon = categoryIcons[category] || ChefHat
                const colors = categoryColors[category] || categoryColors.bases

                return (
                  <div key={category}>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className={`w-5 h-5 ${colors.text}`} />
                      <h4 className="font-display font-semibold text-havn-green-900 capitalize">
                        {category}
                      </h4>
                      <span className="text-sm text-havn-gray-400">
                        ({items.length} items)
                      </span>
                    </div>

                    <div className="space-y-3">
                      {items.map((item, i) => (
                        <BatchItem
                          key={i}
                          item={{ ...item, completed: completed[item.name] }}
                          category={category}
                          onToggleComplete={handleToggleComplete}
                        />
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer Tips */}
        <div className="px-6 py-4 bg-havn-cream-50 border-t border-havn-cream-200 flex-shrink-0">
          <div className="flex items-start gap-2 text-sm text-havn-gray-500">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>
              <strong>Pro tip:</strong> Prep sauces and bases first (they often need cooling time),
              then proteins, and finally vegetables to maintain freshness.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
